import { useState, useEffect, useCallback } from 'react';
import {
  Task,
  TasksResponse,
  TaskFilters,
  CreateTaskInput,
  UpdateTaskInput,
  TaskTenantsListForNetworkResponse,
} from '../types/interfaces/task.types';
import { Severity } from '../types';
import * as taskService from '../services/taskService';
import useSnackbar from './useSnackbar';
import { useAuthContext } from 'contexts/authContext';
import { AxiosError } from 'axios';
import { logger } from 'services/logger';

interface UseTasksReturn {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  tenants?: TaskTenantsListForNetworkResponse;
  selectedTenant: string;
  setSelectedTenant: (tenantId: string) => void;
  fetchTasks: (
    filters?: TaskFilters,
    page?: number,
    pageSize?: number
  ) => Promise<void>;
  createTask: (input: CreateTaskInput) => Promise<Task | null>;
  updateTask: (taskId: string, input: UpdateTaskInput) => Promise<Task | null>;
  deleteTask: (taskId: string) => Promise<boolean>;
  refreshTasks: () => Promise<void>;
}

export const useTasks = (
  initialFilters: TaskFilters = {},
  initialPage = 1,
  initialPageSize = 20
): UseTasksReturn => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [currentFilters, setCurrentFilters] =
    useState<TaskFilters>(initialFilters);
  const [tenants, setTenants] = useState<
    TaskTenantsListForNetworkResponse | undefined
  >();
  const [selectedTenant, setSelectedTenant] = useState('');
  const { user } = useAuthContext();
  const showSnackbar = useSnackbar();

  const fetchTenants = async () => {
    try {
      const tenants = await taskService.getTenants();
      setTenants(tenants);
    } catch (error) {
      logger.error('Failed to fetch tenants:', {
        error: (error as AxiosError).message,
      });
      setError('Failed to load tenants. Please try again later.');
    }
  };

  const fetchTasks = useCallback(
    async (
      filters: TaskFilters = currentFilters,
      newPage: number = page,
      newPageSize: number = pageSize
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response: TasksResponse = await taskService.getTasks(
          filters,
          newPage,
          newPageSize
        );

        setTasks(response.tasks);
        setTotal(response.pagination.total);
        setPage(response.pagination.page);
        setPageSize(response.pagination.pageSize);
        setCurrentFilters(filters);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Failed to fetch tasks';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
      } finally {
        setLoading(false);
      }
    },
    [currentFilters, page, pageSize, showSnackbar]
  );

  const createTask = useCallback(
    async (input: CreateTaskInput): Promise<Task | null> => {
      setLoading(true);
      setError(null);

      try {
        const newTask = await taskService.createTask(input, user!.userActor!);
        showSnackbar('Task created successfully', Severity.success);

        // Refresh tasks list
        await fetchTasks();

        return newTask;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Failed to create task';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchTasks, showSnackbar]
  );

  const updateTask = useCallback(
    async (taskId: string, input: UpdateTaskInput): Promise<Task | null> => {
      setLoading(true);
      setError(null);

      try {
        const updatedTask = await taskService.updateTask(taskId, input);
        showSnackbar('Task updated successfully', Severity.success);

        // Update task in local state
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
        );

        return updatedTask;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Failed to update task';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  const deleteTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await taskService.deleteTask(taskId);
        showSnackbar('Task deleted successfully', Severity.success);

        // Remove task from local state
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
        setTotal((prevTotal) => prevTotal - 1);

        return true;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Failed to delete task';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  const refreshTasks = useCallback(async () => {
    await fetchTasks(currentFilters, page, pageSize);
  }, [fetchTasks, currentFilters, page, pageSize]);

  // Initial fetch
  useEffect(() => {
    fetchTasks();
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tasks,
    loading,
    error,
    total,
    page,
    pageSize,
    tenants,
    selectedTenant,
    setSelectedTenant,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    refreshTasks,
  };
};

export default useTasks;
